import { logger } from '@/lib/logger';
import { Category, Line, Size } from '@/model/schemas';

import { ApiResponse } from '../http/BaseHttpClient';
import { IColor, IProduct } from '../types';
import { AbstractService } from './AbstractService';

/**
 * filter data shape
 * {
 *   categories: FilterDataItem[];
 *   lines: FilterDataItem[];
 *   sizes: FilterDataItem[];
 * }
 */
export type FilterDataItem = { id: string; name?: string; picture?: string; _count?: number };
export type FilterDataList = {
  categories: FilterDataItem[] | [];
  lines: FilterDataItem[] | [];
  sizes: FilterDataItem[] | [];
  [key: string]: FilterDataItem[] | undefined; // Allow for additional filter types in the future
};

const FALLBACK_FILTER_OPTIONS: ApiResponse<FilterDataList> = {
  data: {
    categories: [],
    lines: [],
    sizes: [],
  },
} as ApiResponse<FilterDataList>;

export class ProductService extends AbstractService {
  private static isApiResponse<T>(value: unknown): value is ApiResponse<T> {
    return typeof value === 'object' && value !== null && 'data' in (value as any);
  }

  private static unwrap<T>(value: ApiResponse<T> | T): T {
    if (ProductService.isApiResponse<T>(value)) {
      return value.data as T;
    }
    return value as T;
  }

  async getProducts(params?: Record<string, any>): Promise<IProduct[]> {
    const response = await this.get<IProduct[]>(this.endpoint('products'), params);
    const data = ProductService.unwrap(response);

    logger.debug({ message: 'ProductService:getProducts', count: (data as any)?.length, params });

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch products');
    }

    return data as IProduct[];
  }

  async getPaginatedProducts(params?: Record<string, any>): Promise<ApiResponse<IProduct[]>> {
    const response = await this.http.request<ApiResponse<IProduct[]>>({
      method: 'get',
      url: this.endpoint('products'),
      params,
    });

    const raw = (response as any)?.data;

    // When data is wrapped in { data: [...], meta: {...} }, use the full response to preserve meta.
    // Only fall back to raw when the response is not the wrapped shape.
    const normalized: ApiResponse<IProduct[]> = Array.isArray(raw)
      ? (response as unknown as ApiResponse<IProduct[]>)
      : (raw as ApiResponse<IProduct[]>);

    const payload = normalized;

    logger.debug({ message: 'ProductService:getPaginatedProducts', count: (payload.data as any)?.length, params });

    if ((payload as any)?.error) {
      throw new Error((payload as any).error?.message || 'Failed to fetch products');
    }

    return payload;
  }

  async getProduct(id: string, params?: Record<string, any>): Promise<IProduct> {
    const response = await this.get<IProduct>(this.endpoint('productById', { id }), params);
    const data = ProductService.unwrap(response);

    if ((data as any)?.error) {
      throw new Error((data as any).error?.message || 'Failed to fetch product');
    }

    return data as IProduct;
  }

  async getFilterOptions(): Promise<FilterDataList> {
    try {
      const response = await this.get<ApiResponse<FilterDataList>>(this.endpoint('filterOptions'), undefined, {
        timeout: 30000,
        retries: 2,
        retryDelay: 1000,
      });

      const payload = (response as any)?.data?.data ?? (response as any)?.data ?? response;

      if ((payload as any)?.error) {
        throw new Error((payload as any).error?.message || 'Failed to fetch filter options');
      }

      // Ensure we return the correct shape even if backend changes.
      if (!payload || typeof payload !== 'object') {
        return FALLBACK_FILTER_OPTIONS;
      }

      return payload as FilterDataList;
    } catch (error: any) {
      logger.error({
        message: 'ProductService:getFilterOptions failed, falling back',
        error: error?.message,
      });
      return FALLBACK_FILTER_OPTIONS;
    }
  }

  async getProductLines(): Promise<Line[]> {
    const response = await this.get<Line[]>(this.endpoint('productLines'), { status: "ACTIVE" });
    return ProductService.unwrap(response) as Line[];
  }

  async getProductCategories(): Promise<Category[]> {
    const response = await this.get<Category[]>(this.endpoint('productCategories'), { status: "ACTIVE" });
    return ProductService.unwrap(response) as Category[];
  }

  async getProductSizes(): Promise<Size[]> {
    const response = await this.get<Size[]>(this.endpoint('productSizes'), { status: "ACTIVE" });
    return ProductService.unwrap(response) as Size[];
  }

  async getProductColors(): Promise<IColor[]> {
    const response = await this.get<IColor[]>(this.endpoint('productColors'), { status: "ACTIVE" });
    return ProductService.unwrap(response) as IColor[];
  }

  async getOnSales(params?: Record<string, any>): Promise<IProduct[]> {
    const response = await this.get<IProduct[]>(this.endpoint('in_sale_products'), params);
    return ProductService.unwrap(response) as IProduct[];
  }

  async getOnSalesPaginated(params?: Record<string, any>): Promise<{ products: IProduct[]; meta: any }> {
    const response = await this.http.request<ApiResponse<IProduct[]>>({
      method: 'get',
      url: this.endpoint('in_sale_products'),
      params,
    });
    const raw = (response as any)?.data;
    const payload: any = Array.isArray(raw) ? response : raw;
    const products = (payload?.data ?? payload?.items ?? []) as IProduct[];
    return { products, meta: payload?.meta ?? {} };
  }

  async getNewIn(params?: Record<string, any>): Promise<IProduct[]> {
    const response = await this.get<IProduct[]>(this.endpoint('new_in_products'), params);
    return ProductService.unwrap(response) as IProduct[];
  }

  async getNewInPaginated(params?: Record<string, any>): Promise<{ products: IProduct[]; meta: any }> {
    const response = await this.http.request<ApiResponse<IProduct[]>>({
      method: 'get',
      url: this.endpoint('new_in_products'),
      params,
    });
    const raw = (response as any)?.data;
    const payload: any = Array.isArray(raw) ? response : raw;
    const products = (payload?.data ?? payload?.items ?? []) as IProduct[];
    return { products, meta: payload?.meta ?? {} };
  }
}

export const productService = new ProductService();

export default ProductService;
